import { useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faSend } from '@fortawesome/pro-regular-svg-icons';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateEngageMessageById } from 'api/engage';
import { StepNavigation } from 'assets/js/pages/engage/components/MessageBuilder/StepNavigation';
import { SaveAsTemplateAction } from 'assets/js/pages/engage/components/SaveAsTemplateAction';
import { SendTestMessageDialog } from 'assets/js/pages/engage/components/SendTestMessageDialog';
import { useSendTestMessageConfig } from 'assets/js/pages/engage/useSendTestMessageConfig';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import * as MessageBuilder from 'components/message-builder';
import * as Tooltip from 'components/tooltip';
import { Models } from 'constants/models';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentEngageMessage } from 'customHooks/useCurrentEngageMessage';
import useDebounce from 'customHooks/useDebounce';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { EngageMessage, EngageSender } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const AUTOSAVE_DEBOUNCE = 500;
interface StepTwoFormValues {
    internal_name: string;
    subject: string;
    body: string | null;
    body_json: string | null;
    sender_id: string;
}

const StepTwo = () => {
    const [autoSavedAt, setAutoSavedAt] = useState<DateTime>(DateTime.now());
    const { error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: message } = useCurrentEngageMessage();
    const didInit = useRef(false);

    const variant = message.channel === 'email' ? 'email' : 'sms';

    const methods = Form.useGivebutterForm<StepTwoFormValues>({
        mode: 'all',
        defaultValues: {
            internal_name: message.internal_name ?? '',
            sender_id: message.sender_id?.toString() ?? '',
            subject: message.subject ?? '',
            body: message.body ?? '',
            body_json: message.body_json ?? '',
        },
    });

    const {
        control,
        setError,
        formState: { isDirty },
        handleSubmit,
        setValue,
    } = methods;

    const values = useWatch({ control });

    const {
        mutate: submit,
        isPending,
        isError,
        reset: resetMutation,
    } = useMutation<EngageMessage, GivebutterAxiosError, Partial<StepTwoFormValues>>({
        mutationFn: values => {
            const { subject, body, internal_name, sender_id, body_json } = values;
            const defaultSenderTable =
                message.channel === 'sms' ? Models.MESSAGE_NUMBERS : Models.MESSAGE_DOMAIN_SENDERS;

            const params = {
                subject,
                body,
                body_json,
                internal_name,
                ...(sender_id && {
                    sender: {
                        id: Number(sender_id),
                        table: message.sender_table ?? defaultSenderTable,
                    } as EngageSender,
                }),
            };

            return updateEngageMessageById(accountId, message.id, params);
        },
        onSuccess: response => {
            setAutoSavedAt(DateTime.now());
            queryClient.setQueryData(
                accountQueryKeys.engageMessageById(accountId, message.id),
                response,
            );
            resetMutation();
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            const errors = data?.errors;

            error(data?.message ?? 'Something went wrong. Please try again.');

            if (typeof errors === 'object') {
                for (const field in errors) {
                    const key = field === 'sender.id' ? 'sender_id' : field;
                    setError(key as keyof StepTwoFormValues, {
                        message: errors[key][0],
                    });
                }
            }
        },
    });

    const debouncedValues = useDebounce(values, AUTOSAVE_DEBOUNCE);
    useEffect(() => {
        if (didInit.current) {
            submit(debouncedValues);
        }
    }, [debouncedValues, submit]);

    useEffect(() => {
        if (isDirty) {
            didInit.current = true;
        }
    }, [isDirty]);

    const { isDisabled: isSendTestDisabled, tooltipMessage: sendTestTooltipMessage } =
        useSendTestMessageConfig({
            body: message.body,
            subject: message.subject,
            sender_id: message.sender_id,
            channel: message.channel,
        });

    return (
        <div className="mt-8">
            <Form.Provider {...methods}>
                <form onSubmit={handleSubmit(values => submit(values))}>
                    <MessageBuilder.Root variant={variant} messageId={message.id}>
                        <MessageBuilder.Header>
                            <MessageBuilder.HeaderName />
                            <MessageBuilder.HeaderActions>
                                <Tooltip.Root open={isSendTestDisabled ? undefined : false}>
                                    <SendTestMessageDialog channel={variant} messageId={message.id}>
                                        <Tooltip.Trigger asChild>
                                            <Button
                                                variant="outline"
                                                color="neutral"
                                                size="sm"
                                                className="flex items-center gap-2"
                                                disabled={isSendTestDisabled}
                                            >
                                                <FontAwesomeIcon icon={faSend} size="sm" />
                                                Send a test
                                            </Button>
                                        </Tooltip.Trigger>
                                    </SendTestMessageDialog>
                                    <Tooltip.Portal>
                                        <Tooltip.Content inverse>
                                            <p>{sendTestTooltipMessage}</p>
                                            <Tooltip.Arrow inverse />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>

                                <DropdownMenu.Root>
                                    <DropdownMenu.Trigger asChild>
                                        <Button
                                            variant="outline"
                                            color="neutral"
                                            shape={'square'}
                                            size="sm"
                                            aria-label="Additional actions"
                                        >
                                            <FontAwesomeIcon icon={faEllipsisV} />
                                        </Button>
                                    </DropdownMenu.Trigger>
                                    <DropdownMenu.Portal>
                                        <DropdownMenu.Content side="bottom" align="end">
                                            <SaveAsTemplateAction
                                                channel={message.channel}
                                                disabled={
                                                    !message.body ||
                                                    (message.channel === 'email' &&
                                                        !message.subject)
                                                }
                                                values={{
                                                    subject: message.subject,
                                                    body: message.body,
                                                    body_json: message.body_json,
                                                    internal_name: message.internal_name,
                                                    editor: message.editor,
                                                }}
                                            />
                                        </DropdownMenu.Content>
                                    </DropdownMenu.Portal>
                                </DropdownMenu.Root>
                            </MessageBuilder.HeaderActions>
                        </MessageBuilder.Header>
                        <MessageBuilder.Body>
                            <MessageBuilder.To />
                            <MessageBuilder.From />
                            {variant === 'email' ? (
                                <>
                                    <MessageBuilder.Subject />
                                    <MessageBuilder.EmailFiles />
                                </>
                            ) : null}

                            <MessageBuilder.AdvancedEmailEditor
                                defaultOpen={!message.body}
                                onSave={(pageJson, pageHtml) => {
                                    const options = { shouldDirty: true, shouldTouch: true };
                                    setValue('body', pageHtml, options);
                                    setValue('body_json', pageJson, options);
                                }}
                            />
                            <MessageBuilder.Wysiwyg
                                autoSaving={isError ? null : isPending}
                                autoSavedAt={autoSavedAt}
                            />
                            {variant === 'sms' ? <MessageBuilder.SmsFiles /> : null}
                        </MessageBuilder.Body>
                    </MessageBuilder.Root>
                </form>
            </Form.Provider>
            <StepNavigation />
        </div>
    );
};

export { StepTwo };
